import React from 'react';
import { KatAlert, KatButton, KatSpinner } from '@amzn/katal-react';
import { ApolloError } from 'apollo-client';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import { DashboardSummary } from 'src/generated/graphql';
import { formatFloat, formatInt } from './formatUtils';
import style from './DashboardSummaryCards.module.scss';

type Props = {
  loading: boolean;
  error: ApolloError | undefined;
  data: DashboardSummary | undefined;
  onRetry: () => void;
};

const DashboardSummaryCards: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation();

  if (props.loading && !props.data) {
    return (
      <div className={style.spinnerWrapper}>
        <KatSpinner size="large" />
      </div>
    );
  }

  if (props.error) {
    return (
      <KatAlert
        variant="danger"
        header={t('lui-dashboard-summary-error-header')}
        description={props.error.message}
        persistent={true}
        cta={
          <KatButton variant="primary" onClick={props.onRetry}>
            {t('lui-dashboard-summary-retry')}
          </KatButton>
        }
      />
    );
  }

  if (!props.data) {
    return null;
  }

  const {
    totalRequests,
    inProgress,
    completed,
    failed,
    averageTurnaroundTime,
  } = props.data;

  return (
    <div className={style.container}>
      <div className={style.grid}>
        <div>
          <div className={style.label}>
            {t('lui-dashboard-summary-total-requests')}
          </div>
          <div className={style.value}>{formatInt(totalRequests)}</div>
        </div>
        <div>
          <div className={style.label}>
            {t('lui-dashboard-summary-in-progress')}
          </div>
          <div className={style.value}>{formatInt(inProgress)}</div>
        </div>
        <div>
          <div className={style.label}>
            {t('lui-dashboard-summary-completed')}
          </div>
          <div className={style.value}>{formatInt(completed)}</div>
        </div>
        <div>
          <div className={style.label}>{t('lui-dashboard-summary-failed')}</div>
          <div className={style.value}>{formatInt(failed)}</div>
        </div>
        <div>
          <div className={style.label}>
            {t('lui-dashboard-summary-avg-tat')}
          </div>
          <div className={style.value}>
            {t('lui-dashboard-summary-days-value', {
              value: formatFloat(averageTurnaroundTime),
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardSummaryCards;
